import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import Modal from '../../../../components/Modal'
import Loader from '../../../../components/Loader'
import Toast from '../../../../components/Toast/Toast'
import {
  createCourseQuestion,
  updateCourseQuestion,
} from '../../../../services/Courses'
import { TAGS } from '../../../../services/Tags'
import { globalBootstrap } from '../../../..'

export const QUESTION_FORM_MODAL_ID = 'course-question-form-modal'

const EMPTY_OPTIONS = [
  { option: '', correct: true },
  { option: '', correct: false },
]

// Modal de crear/editar pregunta de examen. Si `question` viene con id → modo
// edición. En edición no se pueden agregar opciones nuevas (el backend espera
// optionId); las opciones se desactivan con su switch.
const QuestionFormModal = ({ courseId, question = null, onClose = () => {} }) => {
  const queryClient = useQueryClient()
  const isEdit = Boolean(question?.id)

  const [questionText, setQuestionText] = useState('')
  const [active, setActive] = useState(true)
  const [options, setOptions] = useState(EMPTY_OPTIONS)

  const resetForm = () => {
    setQuestionText(question?.question || '')
    setActive(question?.active !== false)
    if (question?.id) {
      const list = Array.isArray(question.question_options_courses)
        ? question.question_options_courses
        : []
      setOptions(
        list.map((opt) => ({
          optionId: opt.id,
          option: opt.option || '',
          correct: Boolean(opt.correct),
          active: opt.active !== false,
        }))
      )
    } else {
      setOptions(EMPTY_OPTIONS.map((opt) => ({ ...opt })))
    }
  }

  useEffect(() => {
    resetForm()
  }, [question])

  const closeModal = () => {
    globalBootstrap.Modal.getOrCreateInstance(
      `#${QUESTION_FORM_MODAL_ID}`
    ).hide()
    onClose()
  }

  const handleSuccess = ({ response, res }, successMsg) => {
    if (!response?.ok) {
      Toast({
        icon: 'error',
        title: res?.msg || res?.error || 'No se pudo guardar la pregunta',
      })
      return
    }
    queryClient.invalidateQueries([TAGS.course_questions, courseId])
    Toast({ title: successMsg })
    resetForm()
    closeModal()
  }

  const { mutate: create, isLoading: isCreating } = useMutation(
    createCourseQuestion,
    {
      onSuccess: (data) => handleSuccess(data, 'Pregunta creada correctamente'),
      onError: () =>
        Toast({ icon: 'error', title: 'No se pudo crear la pregunta' }),
    }
  )

  const { mutate: update, isLoading: isUpdating } = useMutation(
    updateCourseQuestion,
    {
      onSuccess: (data) =>
        handleSuccess(data, 'Pregunta actualizada correctamente'),
      onError: () =>
        Toast({ icon: 'error', title: 'No se pudo actualizar la pregunta' }),
    }
  )

  const isLoading = isCreating || isUpdating

  const setOptionField = (index, field, value) => {
    setOptions((prev) =>
      prev.map((opt, i) => (i === index ? { ...opt, [field]: value } : opt))
    )
  }

  // Solo una opción correcta a la vez (comportamiento tipo radio).
  const markCorrect = (index) => {
    setOptions((prev) => prev.map((opt, i) => ({ ...opt, correct: i === index })))
  }

  const addOption = () =>
    setOptions((prev) => [...prev, { option: '', correct: false }])

  const removeOption = (index) => {
    setOptions((prev) => {
      if (prev.length <= 2) return prev
      const next = prev.filter((_, i) => i !== index)
      // Si se eliminó la correcta, la primera pasa a ser la correcta.
      if (!next.some((opt) => opt.correct)) {
        return next.map((opt, i) => (i === 0 ? { ...opt, correct: true } : opt))
      }
      return next
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!questionText.trim()) {
      Toast({ icon: 'error', title: 'Escribe el texto de la pregunta' })
      return
    }

    if (options.some((opt) => !opt.option.trim())) {
      Toast({ icon: 'error', title: 'Completa el texto de todas las opciones' })
      return
    }

    const activeOptions = isEdit
      ? options.filter((opt) => opt.active)
      : options

    if (activeOptions.length < 2) {
      Toast({
        icon: 'error',
        title: 'La pregunta debe tener al menos 2 opciones activas',
      })
      return
    }

    if (!activeOptions.some((opt) => opt.correct)) {
      Toast({
        icon: 'error',
        title: 'Marca una opción activa como la respuesta correcta',
      })
      return
    }

    if (isEdit) {
      update({
        questionId: question.id,
        question: questionText.trim(),
        active,
        options: options.map((opt) => ({
          optionId: opt.optionId,
          option: opt.option.trim(),
          correct: opt.correct,
          active: opt.active,
        })),
      })
    } else {
      create({
        courseId,
        question: questionText.trim(),
        options: options.map((opt) => ({
          option: opt.option.trim(),
          correct: opt.correct,
        })),
      })
    }
  }

  return (
    <Modal
      modalId={QUESTION_FORM_MODAL_ID}
      title={isEdit ? 'Editar pregunta' : 'Crear pregunta'}
      titleClassName="fw-bolder text-on-bg-primary"
      modalSize="modal-lg"
    >
      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label
            className="form-label text-on-bg-primary fw-bold"
            style={{ fontSize: '13px' }}
          >
            Pregunta *
          </label>
          <textarea
            className="form-control"
            rows={2}
            value={questionText}
            onChange={(e) => setQuestionText(e.target.value)}
            placeholder="Escribe la pregunta del examen"
            required
          />
        </div>

        {isEdit && (
          <div className="form-check form-switch mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="question-active-switch"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
            />
            <label
              className="form-check-label text-on-bg-primary"
              htmlFor="question-active-switch"
              style={{ fontSize: '13px' }}
            >
              Pregunta activa
            </label>
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center mb-2">
          <label
            className="form-label text-on-bg-primary fw-bold mb-0"
            style={{ fontSize: '13px' }}
          >
            Opciones * (marca la correcta)
          </label>
          {!isEdit && (
            <button
              type="button"
              className="btn btn-sm btn-white border"
              onClick={addOption}
            >
              + Agregar opción
            </button>
          )}
        </div>

        <div className="d-flex flex-column gap-2 mb-4">
          {options.map((opt, index) => (
            <div
              key={opt.optionId ?? index}
              className="d-flex align-items-center gap-2 flex-wrap"
            >
              <input
                type="radio"
                className="form-check-input mt-0 flex-shrink-0"
                name="correct-option"
                title="Respuesta correcta"
                checked={opt.correct}
                onChange={() => markCorrect(index)}
              />
              <input
                type="text"
                className="form-control"
                style={{ flex: 1, minWidth: 160 }}
                value={opt.option}
                onChange={(e) => setOptionField(index, 'option', e.target.value)}
                placeholder={`Opción ${index + 1}`}
                required
              />
              {isEdit ? (
                <div className="form-check form-switch mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`option-active-${index}`}
                    title="Opción activa"
                    checked={opt.active}
                    onChange={(e) =>
                      setOptionField(index, 'active', e.target.checked)
                    }
                  />
                  <label
                    className="form-check-label text-on-bg-secondary"
                    htmlFor={`option-active-${index}`}
                    style={{ fontSize: '12px' }}
                  >
                    Activa
                  </label>
                </div>
              ) : (
                options.length > 2 && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    style={{ borderRadius: '8px' }}
                    onClick={() => removeOption(index)}
                  >
                    Quitar
                  </button>
                )
              )}
            </div>
          ))}
        </div>

        <div className="d-flex justify-content-end gap-2">
          <button
            type="button"
            className="btn btn-white border"
            data-bs-dismiss="modal"
            onClick={() => {
              resetForm()
              onClose()
            }}
            disabled={isLoading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-bg-CTA-primary d-flex align-items-center justify-content-center gap-2"
            disabled={isLoading}
            style={{ minWidth: '150px' }}
          >
            {isLoading ? (
              <Loader />
            ) : isEdit ? (
              'Guardar cambios'
            ) : (
              'Crear pregunta'
            )}
          </button>
        </div>
      </form>
    </Modal>
  )
}

export default QuestionFormModal
